import {
  CheckmarkCircle02Icon,
  Copy01Icon,
  EyeIcon,
  ViewOffIcon,
} from '@terra/ui-icons';
import { HStack } from '@terra/ui-styled-system';
import { useCallback, type KeyboardEvent } from 'react';
import { toasterApi } from '../toast/ToastProvider';
import { IconButton } from '../../components/icon-button';
import { Input } from '../../components/input';

type SensitiveInputProps = {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  fieldKey: string;
  allowViewingCredentials: boolean;
  onCopy?: (value: string) => Promise<void>;
  copiedState: boolean;
  visibleState: boolean;
  onToggleVisibility: (fieldKey: string) => void;
  onCopySuccess: () => void;
  type?: 'password' | 'text';
  isDefaultValue: boolean;
  disabled?: boolean;
};

export const SensitiveInput = ({
  value,
  placeholder,
  onChange,
  fieldKey,
  allowViewingCredentials,
  onCopy,
  copiedState,
  visibleState,
  onToggleVisibility,
  onCopySuccess,
  type = 'password',
  isDefaultValue,
  disabled = false,
}: SensitiveInputProps) => {
  const shouldShowControls =
    !disabled && allowViewingCredentials && value && !isDefaultValue;
  const inputType = shouldShowControls && visibleState ? 'text' : type;
  const handleCopyClick = useCallback(async () => {
    if (!onCopy || !value) return;

    try {
      await onCopy(value);
      onCopySuccess();
      toasterApi.success({
        title: 'Copied to clipboard',
        description: 'The credential has been copied to your clipboard',
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      toasterApi.error({
        title: 'Failed to copy',
        description: 'Could not copy the credential to clipboard',
      });
    }
  }, [onCopy, value, onCopySuccess]);
  // Handle CMD+C / Ctrl+C for password fields (browsers block native copy for password inputs)
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const isCopyShortcut =
        (event.metaKey || event.ctrlKey) && event.key === 'c';

      if (isCopyShortcut && type === 'password' && value && onCopy) {
        // Prevent default only for password type to allow custom copy
        event.preventDefault();
        handleCopyClick();
      }
    },
    [type, value, onCopy, handleCopyClick],
  );

  return (
    <HStack gap={1}>
      <Input
        placeholder={placeholder}
        value={value}
        type={inputType}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
        flex={1}
        disabled={disabled}
      />
      {shouldShowControls && (
        <HStack gap={1}>
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => onToggleVisibility(fieldKey)}
            aria-label={visibleState ? 'Hide credential' : 'Show credential'}
          >
            {visibleState ? <ViewOffIcon size={16} /> : <EyeIcon size={16} />}
          </IconButton>
          {onCopy && (
            <IconButton
              variant="ghost"
              size="sm"
              onClick={handleCopyClick}
              aria-label="Copy to clipboard"
            >
              {copiedState ? (
                <CheckmarkCircle02Icon size={16} />
              ) : (
                <Copy01Icon size={16} />
              )}
            </IconButton>
          )}
        </HStack>
      )}
    </HStack>
  );
};
